import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AlertController, LoadingController, NavController } from '@ionic/angular';
import { AUTHENTICATE_OPTIONS, AuthResultEnum, BaseAuthService, OAuth2AuthenticateOptions, User } from '@libs/core/auth';
import { ProfileCarrierModel, UserService } from '@libs/core/pt-api';
import { StorageService } from '@libs/core/storage';
import { PrivacyPolicyService } from '@libs/core/privacy-policy';
import { PlatformDetectionService } from '@libs/util/shared';
import { ReplaySubject, combineLatest, lastValueFrom, map } from 'rxjs';

export enum UserRole {
  Driver = 'Driver',
  Carrier = 'Carrier',
}

@Injectable()
export class AuthService extends BaseAuthService<User, User> {
  private storageService: StorageService;

  private readonly privacyPolicyService = inject(PrivacyPolicyService);
  private readonly userService = inject(UserService);
  private readonly loadingController = inject(LoadingController);
  private readonly alertController = inject(AlertController);
  private currentCarrierIdSubject = new ReplaySubject<number>(1);
  private carriersSubject = new ReplaySubject<ProfileCarrierModel[]>(1);
  private isHandlingCarrierDisabled = false;

  readonly carriers$ = this.carriersSubject.asObservable();
  readonly carrierIdSessionKey = 'currentCarrierId';

  readonly currentCarrier$ = combineLatest([this.currentCarrierIdSubject, this.carriersSubject]).pipe(
    map(([currentCarrierId, carriers]) => carriers.find((carrier) => carrier.id === currentCarrierId) as ProfileCarrierModel),
  );

  readonly currentRole$ = combineLatest([this.currentCarrierIdSubject, this.carriersSubject]).pipe(
    map(([currentCarrierId, carriers]) => {
      const carrier = carriers.find((carrier) => carrier.id === currentCarrierId) as ProfileCarrierModel;
      return carrier.isOwner ? UserRole.Carrier : UserRole.Driver;
    }),
  );

  constructor() {
    const platformService = inject(PlatformDetectionService);
    const storageService = inject(StorageService);
    const authOptions = inject<OAuth2AuthenticateOptions>(AUTHENTICATE_OPTIONS);
    const navController = inject(NavController);

    super(storageService, platformService, authOptions, navController);

    this.storageService = storageService;
  }

  override async afterLogin() {
    try {
      const profile = await lastValueFrom(this.userService.apiUsersIdentityMeGet());

      this.privacyPolicyService.setPrivacyPolicy(profile.hasAcceptedPrivacyPolicy);

      if (profile.carriers.length === 0) {
        const loading = await this.loadingController.getTop();
        await loading?.dismiss();

        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: "Nessun vettore attivo disponibile. Contatta l'amministratore.",
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();
        return AuthResultEnum.NOT_AUTHORIZED;
      }

      let persistedCarrierId = await this.storageService.get(this.carrierIdSessionKey);

      if (!persistedCarrierId) {
        persistedCarrierId = profile.carriers[0].id.toString();
      }

      if (!profile.carriers.some((carrier) => carrier.id === +(persistedCarrierId as string))) {
        const fallback = profile.carriers[0].id;

        persistedCarrierId = fallback.toString();
      }

      await this.storageService.set(this.carrierIdSessionKey, persistedCarrierId);

      const carrierId = +(persistedCarrierId as string);

      this.carriersSubject.next(profile.carriers);
      this.currentCarrierIdSubject.next(carrierId);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        const alert = await this.alertController.create({
          message: "Non ci sono vettori associati al tuo account. Contatta l'amministratore.",
          buttons: ['OK'],
        });

        const loading = await this.loadingController.getTop();

        await loading?.dismiss();

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();

        return AuthResultEnum.NOT_AUTHORIZED;
      }
    }
    return;
  }

  async handleCarrierDisabled(): Promise<void> {
    if (this.isHandlingCarrierDisabled) {
      return;
    }

    this.isHandlingCarrierDisabled = true;

    try {
      const profile = await lastValueFrom(this.userService.apiUsersIdentityMeGet());
      const carriers = profile.carriers;

      if (carriers.length > 0) {
        const newCarrier = carriers[0];

        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: `Il vettore selezionato è stato disattivato. Passaggio al vettore ${newCarrier.name}.`,
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.storageService.set(this.carrierIdSessionKey, newCarrier.id.toString());
        window.location.href = '/';
      } else {
        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: 'Il vettore selezionato è stato disattivato. Non ci sono altri vettori disponibili.',
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();
      }
    } finally {
      this.isHandlingCarrierDisabled = false;
    }
  }

  changeCarrier(carrierId: number) {
    this.storageService.set(this.carrierIdSessionKey, carrierId.toString()).then(() => {
      this.currentCarrierIdSubject.next(carrierId);
    });
  }

  override mapUser(rawUser: User): User {
    return rawUser;
  }
}
